import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';

function Managers() {
  const { user } = useAuth();
  const [managers, setManagers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDirectionsModal, setShowDirectionsModal] = useState(false);
  const [editingDirection, setEditingDirection] = useState(null);
  const [directionFormData, setDirectionFormData] = useState({ id: '', name: '', description: '', is_active: true });
  const [expandedDirectionId, setExpandedDirectionId] = useState(null);
  const [showSchedulesModal, setShowSchedulesModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleFormData, setScheduleFormData] = useState({ id: '', name: '', start_time: '', end_time: '', description: '', is_active: true });
  const [expandedScheduleId, setExpandedScheduleId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [managersData, schedulesData, classesData] = await Promise.all([
        api.getManagers({ include_deleted: showDeleted }),
        api.getSchedules(),
        api.getClasses(),
      ]);
      setManagers(managersData);
      setSchedules(schedulesData);
      // Фильтруем только активные классы с to_manager = true (неактивные не показываем в направлениях)
      const managerDirections = classesData.filter(
        cls => cls.class_metadata?.to_manager === true && cls.is_active !== false
      );
      setDirections(managerDirections);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const timezones = [
    { value: 'UTC-12', label: 'UTC-12 (Бейкер-Айленд)' },
    { value: 'UTC-11', label: 'UTC-11 (Американское Самоа)' },
    { value: 'UTC-10', label: 'UTC-10 (Гавайи)' },
    { value: 'UTC-9', label: 'UTC-9 (Аляска)' },
    { value: 'UTC-8', label: 'UTC-8 (Лос-Анджелес)' },
    { value: 'UTC-7', label: 'UTC-7 (Денвер)' },
    { value: 'UTC-6', label: 'UTC-6 (Чикаго)' },
    { value: 'UTC-5', label: 'UTC-5 (Нью-Йорк)' },
    { value: 'UTC-4', label: 'UTC-4 (Каракас)' },
    { value: 'UTC-3', label: 'UTC-3 (Буэнос-Айрес)' },
    { value: 'UTC-2', label: 'UTC-2 (Атлантика)' },
    { value: 'UTC-1', label: 'UTC-1 (Азорские острова)' },
    { value: 'UTC+0', label: 'UTC+0 (Лондон)' },
    { value: 'UTC+1', label: 'UTC+1 (Берлин)' },
    { value: 'UTC+2', label: 'UTC+2 (Каир)' },
    { value: 'UTC+3', label: 'UTC+3 (Москва)' },
    { value: 'UTC+4', label: 'UTC+4 (Дубай)' },
    { value: 'UTC+5', label: 'UTC+5 (Карачи)' },
    { value: 'UTC+6', label: 'UTC+6 (Алматы)' },
    { value: 'UTC+7', label: 'UTC+7 (Бангкок)' },
    { value: 'UTC+8', label: 'UTC+8 (Пекин)' },
    { value: 'UTC+9', label: 'UTC+9 (Токио)' },
    { value: 'UTC+10', label: 'UTC+10 (Сидней)' },
    { value: 'UTC+11', label: 'UTC+11 (Владивасток)' },
    { value: 'UTC+12', label: 'UTC+12 (Окленд)' },
    { value: 'UTC+13', label: 'UTC+13 (Камчатка)' },
    { value: 'UTC+14', label: 'UTC+14 (Кирибати)' }
  ];

  const getBlankForm = () => ({
    name: '',
    second_name: '',
    status: 'active',
    timezone: 'UTC+3',
    start_time: '09:00',
    end_time: '18:00',
    direction_ids: [],
    role: 'manager',
    id_telegram: '',
    id_bitrix: '',
    email: '',
    login: '',
    password: ''
  });

  const handleCreate = () => {
    setSelectedManager(null);
    setFormData(getBlankForm());
    setShowModal(true);
  };

  const handleEdit = (manager) => {
    setSelectedManager(manager);
    // Конвертируем старые строковые ID в числовые, если они существуют в новом списке классов
    const validDirectionIds = (manager.directions || [])
      .map(d => parseInt(d.id))
      .filter(id => !isNaN(id) && directions.some(dir => dir.id === id));

    setFormData({
      id: manager.id,
      name: manager.name || '',
      second_name: manager.second_name || '',
      status: manager.status || 'active',
      timezone: manager.timezone || 'UTC+3',
      start_time: manager.start_time || '09:00',
      end_time: manager.end_time || '18:00',
      direction_ids: validDirectionIds,
      role: manager.role || 'manager',
      id_telegram: manager.id_telegram || '',
      id_bitrix: manager.id_bitrix || '',
      email: manager.e_mail || '',
      login: manager.login || '',
      password: ''
    });
    setShowModal(true);
  };

  const handleDelete = async (managerId) => {
    if (window.confirm('Вы уверены, что хотите удалить этого менеджера?')) {
      try {
        await api.deleteManager(managerId);
        toast.success('Менеджер удален');
        fetchData();
      } catch (error) {
        toast.error(error.message || 'Ошибка при удалении');
      }
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!formData.name || !formData.second_name || !formData.role) {
        toast.error('Поля Имя, Фамилия и Роль обязательны для заполнения');
        return;
    }

    const payload = {
      name: formData.name,
      second_name: formData.second_name,
      status: formData.status,
      timezone: formData.timezone || 'UTC+3',
      start_time: formData.start_time || '09:00',
      end_time: formData.end_time || '18:00',
      direction_ids: formData.direction_ids?.map(id => String(id)) || [],
      role: formData.role,
      id_telegram: formData.id_telegram ? parseInt(formData.id_telegram) : undefined,
      id_bitrix: formData.id_bitrix ? parseInt(formData.id_bitrix) : undefined,
      e_mail: formData.email || undefined,
      login: formData.login || undefined,
      password: formData.password || undefined,
    };
    
    // Удаляем undefined значения
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    try {
      if (selectedManager) {
        await api.updateManager(selectedManager.id, payload);
        toast.success('Менеджер обновлен');
      } else {
        await api.createManager(payload);
        toast.success('Менеджер создан');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  
  const handleDirectionChange = (directionId, isChecked) => {
    setFormData(prev => {
        const currentIds = prev.direction_ids || [];
        if (isChecked) {
            return {...prev, direction_ids: [...currentIds, directionId]};
        } else {
            return {...prev, direction_ids: currentIds.filter(id => id !== directionId)};
        }
    });
  };

  const allRoleOptions = [
    { value: 'manager', label: 'Менеджер' },
    { value: 'administrator', label: 'Администратор' },
    { value: 'super_admin', label: 'Супер Админ' },
    { value: 'rop', label: 'Руководитель отдела продаж' },
    { value: 'smm_manager', label: 'SMM Менеджер' }
  ];

  // Фильтруем доступные роли по can_create_roles текущего пользователя
  const roleOptions = useMemo(() => {
    const canCreate = user?.can_create_roles || user?.can_create_roles_raw;
    if (!canCreate) return allRoleOptions;
    const allowed = Array.isArray(canCreate)
      ? canCreate.map(r => r.toLowerCase())
      : canCreate.split(',').map(r => r.trim().toLowerCase());
    return allRoleOptions.filter(opt => allowed.includes(opt.value));
  }, [user]);

  const statusOptions = [
    { value: 'active', label: 'Активен' },
    { value: 'inactive', label: 'Неактивен' },
    { value: 'on_vacation', label: 'В отпуске' },
    { value: 'sick_leave', label: 'Больничный' },
    { value: 'fired', label: 'Уволен' },
    { value: 'deleted', label: 'Удален' }
  ];

  const handleRestore = async (managerId) => {
    if (window.confirm('Вы уверены, что хотите восстановить этого менеджера?')) {
      try {
        await api.updateManager(managerId, { status: 'active' });
        toast.success('Менеджер восстановлен');
        fetchData();
      } catch (error) {
        toast.error(error.message || 'Ошибка при восстановлении');
      }
    }
  };

  const handleOpenDirectionsModal = () => {
    setShowDirectionsModal(true);
    setEditingDirection(null);
    setDirectionFormData({ id: '', name: '', description: '', is_active: true });
  };

  const handleEditDirection = (direction) => {
    setEditingDirection(direction);
    setExpandedDirectionId(direction.id);
    setDirectionFormData({
      id: direction.id,
      name: direction.name,
      description: direction.description || '',
      is_active: direction.is_active
    });
  };

  const toggleDirectionAccordion = (directionId) => {
    if (expandedDirectionId === directionId) {
      setExpandedDirectionId(null);
      setEditingDirection(null);
    } else {
      const direction = directions.find(d => d.id === directionId);
      if (direction) {
        handleEditDirection(direction);
      }
    }
  };

  const handleDeleteDirection = async (directionId) => {
    if (window.confirm('Вы уверены, что хотите удалить это направление?')) {
      try {
        await api.deleteDirection(directionId);
        toast.success('Направление удалено');
        fetchData();
      } catch (error) {
        toast.error(error.message || 'Ошибка при удалении направления');
      }
    }
  };

  const handleDirectionFormSubmit = async (event) => {
    event.preventDefault();

    if (!directionFormData.id || !directionFormData.name) {
      toast.error('ID и название обязательны для заполнения');
      return;
    }

    try {
      if (editingDirection) {
        await api.updateDirection(directionFormData.id, {
          name: directionFormData.name,
          description: directionFormData.description,
          is_active: directionFormData.is_active
        });
        toast.success('Направление обновлено');
        setExpandedDirectionId(null);
      } else {
        await api.createDirection(directionFormData);
        toast.success('Направление создано');
      }
      setEditingDirection(null);
      setDirectionFormData({ id: '', name: '', description: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении направления');
    }
  };

  const handleOpenSchedulesModal = () => {
    setShowSchedulesModal(true);
    setEditingSchedule(null);
    setScheduleFormData({ id: '', name: '', start_time: '', end_time: '', description: '', is_active: true });
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setExpandedScheduleId(schedule.id);
    setScheduleFormData({
      id: schedule.id,
      name: schedule.name,
      start_time: schedule.start_time || '',
      end_time: schedule.end_time || '',
      description: schedule.description,
      is_active: schedule.is_active
    });
  };

  const toggleScheduleAccordion = (scheduleId) => {
    if (expandedScheduleId === scheduleId) {
      setExpandedScheduleId(null);
      setEditingSchedule(null);
    } else {
      const schedule = schedules.find(s => s.id === scheduleId);
      if (schedule) {
        handleEditSchedule(schedule);
      }
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот график?')) {
      try {
        await api.deleteSchedule(scheduleId);
        toast.success('График удален');
        fetchData();
      } catch (error) {
        toast.error(error.message || 'Ошибка при удалении графика');
      }
    }
  };

  const handleScheduleFormSubmit = async (event) => {
    event.preventDefault();

    if (!scheduleFormData.id || !scheduleFormData.name) {
      toast.error('ID и название обязательны для заполнения');
      return;
    }

    try {
      const payload = {
        name: scheduleFormData.name,
        description: scheduleFormData.description,
        is_active: scheduleFormData.is_active,
        start_time: scheduleFormData.start_time || null,
        end_time: scheduleFormData.end_time || null
      };

      if (editingSchedule) {
        await api.updateSchedule(scheduleFormData.id, payload);
        toast.success('График обновлен');
        setExpandedScheduleId(null);
      } else {
        await api.createSchedule({
          id: scheduleFormData.id,
          ...payload
        });
        toast.success('График создан');
      }
      setEditingSchedule(null);
      setScheduleFormData({ id: '', name: '', start_time: '', end_time: '', description: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении графика');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Таблица менеджеров</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className={`btn ${showDeleted ? 'btn-primary' : 'btn-secondary'}`}
          >
            {showDeleted ? 'Скрыть удаленных' : 'Показать удаленных'}
          </button>
          <button onClick={handleCreate} className="btn btn-primary">Добавить менеджера</button>
        </div>
      </div>

      {loading ? (
        <p>Загрузка...</p>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th className="px-4 py-2">Имя</th>
                <th className="px-4 py-2 text-center">Часовой пояс</th>
                <th className="px-4 py-2 text-center">Время работы</th>
                <th className="px-4 py-2 text-center">Направления</th>
                <th className="px-4 py-2 text-center">Роль</th>
                <th className="px-4 py-2 text-center">Статус</th>
                <th className="px-4 py-2 text-center">Действия</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((manager) => (
                <tr key={manager.id} className="align-middle">
                  <td className="px-4 py-2">{`${manager.name} ${manager.second_name || ''}`.trim()}</td>
                  <td className="px-4 py-2 text-center">{manager.timezone || 'Н/Д'}</td>
                  <td className="px-4 py-2 text-center">
                    {manager.start_time && manager.end_time
                      ? `${manager.start_time.slice(0, 5)} - ${manager.end_time.slice(0, 5)}`
                      : 'Н/Д'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {(manager.directions || []).filter(d => d.is_active !== false).length} / {directions.length}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {allRoleOptions.find(r => r.value === manager.role)?.label || manager.role}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`status-badge ${'status-' + manager.status}`}>
                      {statusOptions.find(s => s.value === manager.status)?.label || manager.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        <button onClick={() => handleEdit(manager)} className="btn btn-outline btn-small">Редактировать</button>
                        {manager.status === 'deleted' ? (
                          <button onClick={() => handleRestore(manager.id)} className="btn btn-primary btn-small">Восстановить</button>
                        ) : (
                          <button onClick={() => handleDelete(manager.id)} className="btn btn-danger btn-small">Удалить</button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedManager ? 'Редактировать менеджера' : 'Добавить менеджера'}>
          <form onSubmit={handleSubmit} className="manager-form" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="form-grid">
              <div className="form-column">
                <div className="form-group">
                  <label>Имя *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div className="form-group">
                  <label>Id telegram</label>
                  <input
                    type="text"
                    value={formData.id_telegram || ''}
                    onChange={(e) => handleFormChange('id_telegram', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div className="form-group">
                  <label>Почта</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div className="form-group">
                  <label>Роль *</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => handleFormChange('role', e.target.value)}
                      style={{
                        width: '100%',
                        height: '38px',
                        padding: '8px 32px 8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        appearance: 'none',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    >
                      {roleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }}>
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-column">
                <div className="form-group">
                  <label>Фамилия *</label>
                  <input
                    type="text"
                    required
                    value={formData.second_name}
                    onChange={(e) => handleFormChange('second_name', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div className="form-group">
                  <label>Id Bitrix</label>
                  <input
                    type="text"
                    value={formData.id_bitrix || ''}
                    onChange={(e) => handleFormChange('id_bitrix', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div className="form-group">
                  <label>Статус</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={formData.status}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                      style={{
                        width: '100%',
                        height: '38px',
                        padding: '8px 32px 8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        appearance: 'none',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    >
                      {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }}>
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Логин</label>
                <input
                  type="text"
                  value={formData.login || ''}
                  onChange={(e) => handleFormChange('login', e.target.value)}
                  placeholder="Логин сотрудника"
                  style={{
                    width: '100%',
                    height: '38px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>{selectedManager ? 'Новый пароль' : 'Пароль'}</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  placeholder={selectedManager ? 'Оставьте пустым, чтобы не менять' : 'Пароль сотрудника'}
                  style={{
                    width: '100%',
                    height: '38px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Рабочее время
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Часовой пояс</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={formData.timezone || 'UTC+3'}
                      onChange={(e) => handleFormChange('timezone', e.target.value)}
                      style={{
                        width: '100%',
                        height: '38px',
                        padding: '8px 32px 8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        appearance: 'none',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    >
                      {timezones.map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }}>
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Начало</label>
                  <input
                    type="time"
                    value={formData.start_time || '09:00'}
                    onChange={(e) => handleFormChange('start_time', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Конец</label>
                  <input
                    type="time"
                    value={formData.end_time || '18:00'}
                    onChange={(e) => handleFormChange('end_time', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              </div>
              <small style={{ display: 'block', marginTop: '4px', fontSize: '0.75rem', color: '#6b7280' }}>
                Сейчас на сервере: {new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </small>
            </div>

            <div className="roles-section">
              <h4>Направления</h4>
              <div className="roles-grid">
                {directions.map(direction => (
                  <label key={direction.id} className="role-item">
                    <input
                      type="checkbox"
                      checked={formData.direction_ids?.includes(direction.id)}
                      onChange={(e) => handleDirectionChange(direction.id, e.target.checked)}
                    />
                    <span>{direction.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Отмена</button>
              <button type="submit" className="btn btn-primary">{selectedManager ? 'Обновить' : 'Создать'}</button>
            </div>
          </form>
        </Modal>
      )}

      {showDirectionsModal && (
        <Modal isOpen={showDirectionsModal} onClose={() => setShowDirectionsModal(false)} title="Управление направлениями">
          <div className="directions-management">
            <div className="directions-list" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Существующие направления</h3>
              {directions.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px' }}>Нет направлений</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {directions.map((direction) => (
                    <div key={direction.id} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                      <div
                        onClick={() => toggleDirectionAccordion(direction.id)}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: expandedDirectionId === direction.id ? '#f3f4f6' : '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '18px' }}>{expandedDirectionId === direction.id ? '▼' : '▶'}</span>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>{direction.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {direction.id}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', padding: '2px 8px', backgroundColor: direction.is_active ? '#d1fae5' : '#fee2e2', color: direction.is_active ? '#065f46' : '#991b1b', borderRadius: '4px' }}>
                            {direction.is_active ? 'Активно' : 'Неактивно'}
                          </span>
                        </div>
                      </div>

                      {expandedDirectionId === direction.id && editingDirection && (
                        <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                          <form onSubmit={handleDirectionFormSubmit}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <div className="form-group" style={{ marginBottom: '0' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Название *</label>
                                <input
                                  type="text"
                                  required
                                  value={directionFormData.name}
                                  onChange={(e) => setDirectionFormData({ ...directionFormData, name: e.target.value })}
                                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                />
                              </div>

                              <div className="form-group" style={{ marginBottom: '0' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Описание</label>
                                <textarea
                                  value={directionFormData.description}
                                  onChange={(e) => setDirectionFormData({ ...directionFormData, description: e.target.value })}
                                  rows="2"
                                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                />
                              </div>

                              <div className="form-group" style={{ marginBottom: '0' }}>
                                <label className="role-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                  <input
                                    type="checkbox"
                                    checked={directionFormData.is_active}
                                    onChange={(e) => setDirectionFormData({ ...directionFormData, is_active: e.target.checked })}
                                  />
                                  <span>Активно</span>
                                </label>
                              </div>

                              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button type="submit" className="btn btn-primary btn-small">Сохранить</button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteDirection(direction.id)}
                                  className="btn btn-danger btn-small"
                                >
                                  Удалить
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setExpandedDirectionId(null);
                                    setEditingDirection(null);
                                  }}
                                  className="btn btn-outline btn-small"
                                >
                                  Отмена
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="direction-form" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Добавить новое направление</h3>
              <form onSubmit={handleDirectionFormSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>ID направления *</label>
                    <input
                      type="text"
                      required
                      value={directionFormData.id}
                      onChange={(e) => setDirectionFormData({ ...directionFormData, id: e.target.value })}
                      placeholder="например: retail"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                      onFocus={() => {
                        setExpandedDirectionId(null);
                        setEditingDirection(null);
                      }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Название *</label>
                    <input
                      type="text"
                      required
                      value={directionFormData.name}
                      onChange={(e) => setDirectionFormData({ ...directionFormData, name: e.target.value })}
                      placeholder="например: Розница"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Описание</label>
                    <textarea
                      value={directionFormData.description}
                      onChange={(e) => setDirectionFormData({ ...directionFormData, description: e.target.value })}
                      placeholder="Описание направления"
                      rows="2"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label className="role-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={directionFormData.is_active}
                        onChange={(e) => setDirectionFormData({ ...directionFormData, is_active: e.target.checked })}
                      />
                      <span>Активно</span>
                    </label>
                  </div>

                  <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                    <button type="submit" className="btn btn-primary">Создать направление</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal>
      )}

      {showSchedulesModal && (
        <Modal isOpen={showSchedulesModal} onClose={() => setShowSchedulesModal(false)} title="Управление графиками">
          <div className="schedules-management">
            <div className="schedules-list" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Существующие графики</h3>
              {schedules.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px' }}>Нет графиков</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {schedules.map((schedule) => (
                    <div key={schedule.id} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                      <div
                        onClick={() => toggleScheduleAccordion(schedule.id)}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: expandedScheduleId === schedule.id ? '#f3f4f6' : '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '18px' }}>{expandedScheduleId === schedule.id ? '▼' : '▶'}</span>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>{schedule.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              ID: {schedule.id} | {schedule.start_time || '00:00'} - {schedule.end_time || '23:59'}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', padding: '2px 8px', backgroundColor: schedule.is_active ? '#d1fae5' : '#fee2e2', color: schedule.is_active ? '#065f46' : '#991b1b', borderRadius: '4px' }}>
                            {schedule.is_active ? 'Активен' : 'Неактивен'}
                          </span>
                        </div>
                      </div>

                      {expandedScheduleId === schedule.id && editingSchedule && (
                        <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                          <form onSubmit={handleScheduleFormSubmit}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <div className="form-group" style={{ marginBottom: '0' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Название *</label>
                                <input
                                  type="text"
                                  required
                                  value={scheduleFormData.name}
                                  onChange={(e) => setScheduleFormData({ ...scheduleFormData, name: e.target.value })}
                                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                />
                              </div>

                              <div style={{ display: 'flex', gap: '12px' }}>
                                <div className="form-group" style={{ marginBottom: '0', flex: 1 }}>
                                  <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Время начала</label>
                                  <input
                                    type="time"
                                    value={scheduleFormData.start_time}
                                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, start_time: e.target.value })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                </div>

                                <div className="form-group" style={{ marginBottom: '0', flex: 1 }}>
                                  <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Время окончания</label>
                                  <input
                                    type="time"
                                    value={scheduleFormData.end_time}
                                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, end_time: e.target.value })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                </div>
                              </div>

                              <div className="form-group" style={{ marginBottom: '0' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Описание</label>
                                <textarea
                                  value={scheduleFormData.description}
                                  onChange={(e) => setScheduleFormData({ ...scheduleFormData, description: e.target.value })}
                                  rows="2"
                                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                />
                              </div>

                              <div className="form-group" style={{ marginBottom: '0' }}>
                                <label className="role-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                  <input
                                    type="checkbox"
                                    checked={scheduleFormData.is_active}
                                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, is_active: e.target.checked })}
                                  />
                                  <span>Активен</span>
                                </label>
                              </div>

                              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button type="submit" className="btn btn-primary btn-small">Сохранить</button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSchedule(schedule.id)}
                                  className="btn btn-danger btn-small"
                                >
                                  Удалить
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setExpandedScheduleId(null);
                                    setEditingSchedule(null);
                                  }}
                                  className="btn btn-outline btn-small"
                                >
                                  Отмена
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="schedule-form" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Добавить новый график</h3>
              <form onSubmit={handleScheduleFormSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>ID графика *</label>
                    <input
                      type="text"
                      required
                      value={scheduleFormData.id}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, id: e.target.value })}
                      placeholder="например: remote"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                      onFocus={() => {
                        setExpandedScheduleId(null);
                        setEditingSchedule(null);
                      }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Название *</label>
                    <input
                      type="text"
                      required
                      value={scheduleFormData.name}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, name: e.target.value })}
                      placeholder="например: Удаленная работа"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div className="form-group" style={{ marginBottom: '0', flex: 1 }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Время начала</label>
                      <input
                        type="time"
                        value={scheduleFormData.start_time}
                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, start_time: e.target.value })}
                        style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: '0', flex: 1 }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Время окончания</label>
                      <input
                        type="time"
                        value={scheduleFormData.end_time}
                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, end_time: e.target.value })}
                        style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Описание</label>
                    <textarea
                      value={scheduleFormData.description}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, description: e.target.value })}
                      placeholder="Описание графика работы"
                      rows="2"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label className="role-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={scheduleFormData.is_active}
                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, is_active: e.target.checked })}
                      />
                      <span>Активен</span>
                    </label>
                  </div>

                  <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                    <button type="submit" className="btn btn-primary">Создать график</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Managers;
